// --- START OF FILE AdminPortal.js ---
import React, { useState, useEffect } from 'react';
import { UserProvider, useUser } from '../UserContext';
import SchoolDirectory from './SchoolDirectory';
import ReportEditor from './ReportEditor'; // ADDED THIS IMPORT
import SchoolDossier from './SchoolDossier';
import StaffManagement from './StaffManagement';
import PortalLanguages from './PortalLanguages';
import AdminAiPipeline from './AdminAiPipeline';
import AuditorProfile from './AuditorProfile';
import AdminPricing from './AdminPricing';
import AdminFinance from './AdminFinance';
import StaffEarnings from './StaffEarnings';
import AdminPublicShowcase from './AdminPublicShowcase';
import AdminSupportAssistant from './AdminSupportAssistant';
import '../admin-styles.css'; 
import AuditTrail from './AuditTrail';
import CommsLab from './CommsLab'; // New Diagnostic Tool
import OverviewDashboard from './OverviewDashboard';
import SupportHelpButton from '../components/SupportHelpButton';
import SupportIssueModal from '../components/SupportIssueModal';
import { adminServices } from '../services/adminServices';
import { db } from '../firebase-config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

// Line 11
const AdminPortalContent = () => {
  const { user, staffRecord, permissions, isApproved, loading, error, login, logout } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [editingSchoolId, setEditingSchoolId] = useState(null);
  const [dossierSchoolId, setDossierSchoolId] = useState(null);
  const [lastActiveId, setLastActiveId] = useState(null); // Track context for "Back"
  const [toast, setToast] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [showBellDropdown, setShowBellDropdown] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const navigateFromActionPath = (actionPath) => {
    if (!actionPath) return false;
    try {
      const url = new URL(actionPath, window.location.origin);
      const tab = url.searchParams.get('tab');
      const schoolId = url.searchParams.get('schoolId');
      const mode = (url.searchParams.get('mode') || '').toLowerCase();
      if (tab) setActiveTab(tab);
      if (schoolId) setLastActiveId(schoolId);
      if (mode === 'report' && schoolId) {
        setDossierSchoolId(null);
        setEditingSchoolId(schoolId);
      } else if (mode === 'dossier' && schoolId) {
        // Legacy notifications/emails used `mode=dossier`. Never open the dossier overlay from links —
        // only registry (expanded row) or report editor are supported entry points.
        setEditingSchoolId(null);
        setDossierSchoolId(null);
        setActiveTab('registry');
        setLastActiveId(schoolId);
      } else {
        setEditingSchoolId(null);
        setDossierSchoolId(null);
      }
      return true;
    } catch {
      return false;
    }
  };

  const isTaskActionable = (task) => {
    if (!task) return false;
    if (task.isAction === false) return false;
    if (task.type === 'RESOLUTION') return false;
    return true;
  };

  const bellTrafficLightColor = (task) => {
    if (!isTaskActionable(task)) return '#94a3b8';
    if (task.priority === 'red') return '#f87171';
    if (task.priority === 'amber') return '#fbbf24';
    return '#94a3b8';
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest('.notification-bell-wrapper')) setShowBellDropdown(false);
      if (!e.target.closest('.support-help-wrapper')) setShowHelpMenu(false);
    };
    if (showBellDropdown || showHelpMenu) document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, [showBellDropdown, showHelpMenu]);

  useEffect(() => {
    // Support deep links from admin notification emails (tab + school + mode).
    navigateFromActionPath(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    if (!user || !permissions) return;

    // Build list of target IDs: individual UID, "ALL", and any PERM_ tags they qualify for
    const activePermissions = Object.keys(permissions)
      .filter(key => permissions[key])
      .map(key => `PERM_${key}`);
    const notificationTargets = [user.uid, "ALL", ...activePermissions];

    const q = query(
      collection(db, "notifications"), 
      where("uid", "in", notificationTargets) 
    );

    const unsub = onSnapshot(q, async (snapshot) => {
      // Fetch dynamic state tasks
      const stateTasks = await adminServices.getGlobalTasks(user, permissions);
      
      // Clean stale diagnostic test alerts and keep them out of staff action surfaces.
      const staleDiagnostics = snapshot.docs.filter(d => {
        const x = d.data() || {};
        return x.type === 'DIAGNOSTIC' || x.linkId === 'DIAGNOSTIC_TASK';
      });
      if (staleDiagnostics.length > 0) {
        await Promise.all(staleDiagnostics.map(d => adminServices.dismissNotification(d.id)));
      }

      // Map Firestore notifications to the same UI structure
      const eventNotifications = snapshot.docs
        .filter(d => {
          const x = d.data() || {};
          return !(x.type === 'DIAGNOSTIC' || x.linkId === 'DIAGNOSTIC_TASK');
        })
        .map(doc => ({
          ...doc.data(),
          id: doc.data().linkId || doc.id, // For navigation
          docId: doc.id,                   // For dismissal
          isNotification: true,
          bucket:
            doc.data()?.isAction === false || doc.data()?.type === 'RESOLUTION'
              ? 'notification'
              : 'action'
        }));

      const normalizedStateTasks = stateTasks.map((t) => ({
        ...t,
        isNotification: false,
        bucket: 'action'
      }));

      const bellSortKey = (t) => {
        const created = t.createdAt?.toMillis ? t.createdAt.toMillis() : 0;
        const bucket = t.bucket === 'action' ? 0 : 1; // actions first, notifications after
        const priRank = t.priority === 'red' ? 0 : t.priority === 'amber' ? 1 : 2;
        // Within actions: red first, then amber, then newest first within each band.
        return bucket * 1e18 + priRank * 1e15 + created;
      };

      setPendingTasks([...normalizedStateTasks, ...eventNotifications].sort((a, b) => bellSortKey(a) - bellSortKey(b)));
    });

    return () => unsub();
  }, [user, permissions, activeTab]);

  const handleDismissTask = async (e, task) => {
    e.stopPropagation();
    if (task.docId) {
      await adminServices.dismissNotification(task.docId);
    }
  };

  const getNotificationScanTargets = () => {
    const activePermissions = Object.keys(permissions || {})
      .filter((key) => permissions[key])
      .map((key) => `PERM_${key}`);
    return [user.uid, "ALL", ...activePermissions];
  };

  const handleClearAllInformationalNotifications = async () => {
    try {
      await adminServices.dismissMyInformationalNotifications(getNotificationScanTargets());
      setShowBellDropdown(false);
      setToast({ message: "Notifications cleared", type: "success" });
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast({ message: e?.message || "Failed to clear notifications", type: "error" });
      setTimeout(() => setToast(null), 3500);
    }
  };

  /** Leave dossier / report editor so `activeTab` content is visible (sidebar must clear overlay state). */
  const navigateToTab = (tab) => {
    setEditingSchoolId(null);
    setDossierSchoolId(null);
    setActiveTab(tab);
    setNavOpen(false);
  };

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('admin-nav-lock');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('admin-nav-lock');
    };
  }, [navOpen]);

  const handleTaskAction = (task) => {
    if (!isTaskActionable(task)) return;
    if (navigateFromActionPath(task.actionPath)) return;
    if (task.type === 'STAFF_APPROVAL' || task.type === 'PROFILE_REVIEW') {
      navigateToTab('staff');
    } else if (['PEER_REVIEW', 'REVISION_REQUIRED', 'APPEAL_RESPONSE'].includes(task.type)) {
      handleEditReport(task.id);
    } else if (['ACTIVATE_SCHOOL', 'ASSIGN_AUDITOR', 'RUN_AI', 'AUDITOR_ASSIGNED'].includes(task.type)) {
      navigateToTab('registry');
      setLastActiveId(task.id);
    } else if (task.type === 'ACCEPTED') {
      handleEditReport(task.id);
    } else if (task.type === 'FINALISED') {
      navigateToTab('registry');
      setLastActiveId(task.id);
    }
  };

  const handleBellItemClick = (task) => {
    if (!isTaskActionable(task)) return;
    handleTaskAction(task);
    setShowBellDropdown(false);
  };

  const openSupportAssistant = () => {
    navigateToTab('support-assistant');
    setShowHelpMenu(false);
  };

  const adminSupportContext = {
    route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/admin',
    actorType: 'staff',
    role: staffRecord?.role || null,
    permissionSnapshot: permissions,
    activeTab,
    editingSchoolId,
    dossierSchoolId,
    schoolId: editingSchoolId || dossierSchoolId || lastActiveId || null,
    reportId: editingSchoolId || null,
    reporterName: user?.displayName || null,
    reporterEmail: user?.email || null,
  };

  if (loading) return <div className="admin-placeholder">Verifying Credentials...</div>;

  if (!user) {
    return (
      <div className="admin-auth-gate">
        <div className="auth-card">
          <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Admin" />
          <h2>Accreditation Admin Hub</h2>
          <p>Authentication required (@ifip.group)</p>
          {error && <div className="auth-error">{error}</div>}
          <button onClick={login} className="btn-refinery-run" style={{ width: '100%', marginTop: '20px' }}>
            Sign in with your IFIP Email
          </button>
        </div>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <div className="admin-auth-gate">
        <div className="auth-card" style={{ maxWidth: '600px', textAlign: 'left' }}>
          <h2 style={{ textAlign: 'center' }}>Professional Onboarding</h2>
          <p style={{ textAlign: 'center', marginBottom: '30px' }}>
            Welcome, {user.displayName}. To access the IFIP Admin Hub, please complete your accreditor profile for review.
          </p>
          
          <div style={{ background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '10px' }}>
            <AuditorProfile onboardingMode={true} />
          </div>

          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button onClick={logout} className="btn-secondary" style={{ width: 'auto' }}>Sign Out</button>
          </div>
        </div>
      </div>
    );
  }

  const showToast = (message, type = 'success') => {

    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleEditReport = (schoolId) => {
    setLastActiveId(schoolId);
    setDossierSchoolId(null);
    setEditingSchoolId(schoolId);
  };

  const handleOpenDossier = (schoolId) => {
    setLastActiveId(schoolId);
    setDossierSchoolId(schoolId);
  };

  const closeEditor = () => {
    setEditingSchoolId(null);
  };

  return (
    <div className={`admin-wrapper${navOpen ? ' admin-wrapper--nav-open' : ''}`}>
      {toast && (
        <div className={`admin-toast ${toast.type}`}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.message}
        </div>
      )}
      <button
        type="button"
        className="admin-nav-backdrop"
        aria-label="Close navigation"
        tabIndex={navOpen ? 0 : -1}
        onClick={() => setNavOpen(false)}
      />
      <aside className="admin-sidebar" id="admin-sidebar-nav" aria-hidden={false}>
        <div className="admin-logo">
          <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Admin" />
          <span>Admin Hub</span>
          <button
            type="button"
            className="admin-nav-close"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          >
            ×
          </button>
        </div>
        <nav aria-label="Admin sections">
          <button 
            className={activeTab === 'overview' ? 'active' : ''} 
            onClick={() => navigateToTab('overview')}
          >
            Action Center
          </button>
          
          <button 
            className={activeTab === 'registry' ? 'active' : ''} 
            onClick={() => navigateToTab('registry')}
          >
            School Directory
          </button>

          {permissions.canManagePayments && (
            <button 
              className={activeTab === 'payments' ? 'active' : ''} 
              onClick={() => navigateToTab('payments')}
            >
              Manage Fees and Offers
            </button>
          )}

          {permissions.canManagePayments && (
            <button
              className={activeTab === 'finance' ? 'active' : ''}
              onClick={() => navigateToTab('finance')}
            >
              Finance Ledger
            </button>
          )}

          {isApproved && (
            <button
              className={activeTab === 'my-earnings' ? 'active' : ''}
              onClick={() => navigateToTab('my-earnings')}
            >
              My Earnings
            </button>
          )}

          {permissions.canManagePublicShowcase && (
            <button
              className={activeTab === 'public-showcase' ? 'active' : ''}
              onClick={() => navigateToTab('public-showcase')}
            >
              Flagship Schools
            </button>
          )}

          {permissions.canViewAuditTrail && (
            <button 
              className={activeTab === 'audit-trail' ? 'active' : ''} 
              onClick={() => navigateToTab('audit-trail')}
            >
              System Logs
            </button>
          )}

          {permissions.canManageStaff && (
            <button 
              className={activeTab === 'staff' ? 'active' : ''} 
              onClick={() => navigateToTab('staff')}
            >
              Staff Management
            </button>
          )}

          {permissions.canManagePortalLanguages && (
            <button
              className={activeTab === 'portal-languages' ? 'active' : ''}
              onClick={() => navigateToTab('portal-languages')}
            >
              Portal Languages
            </button>
          )}

          {permissions.canManageStaff && (
            <button
              className={activeTab === 'ai-pipeline' ? 'active' : ''}
              onClick={() => navigateToTab('ai-pipeline')}
            >
              AI Pipeline
            </button>
          )}

          {permissions.canUseSupportAssistant && (
            <button
              className={activeTab === 'support-assistant' ? 'active' : ''}
              onClick={() => navigateToTab('support-assistant')}
            >
              Support Assistant
            </button>
          )}

          {permissions.canManageStaff && (
            <button 
              className={activeTab === 'emails' ? 'active' : ''} 
              onClick={() => navigateToTab('emails')}
            >
              Email Checker
            </button>
          )}

          <button
            className={`admin-nav-profile${activeTab === 'profile' ? ' active' : ''}`}
            onClick={() => navigateToTab('profile')}
          >
            My Accreditor Profile
          </button>
        </nav>
        <div className="admin-footer">
          v2.0.1-Stable
        </div>
      </aside>

      <main className="admin-content">
        <header className="admin-header">
          <div className="admin-header-title-row">
            <button
              type="button"
              className="admin-nav-toggle"
              aria-label="Open navigation"
              aria-expanded={navOpen}
              aria-controls="admin-sidebar-nav"
              onClick={() => setNavOpen(true)}
            >
              <span className="admin-nav-toggle-bar" aria-hidden="true" />
              <span className="admin-nav-toggle-bar" aria-hidden="true" />
              <span className="admin-nav-toggle-bar" aria-hidden="true" />
            </button>
            <h2>
              {editingSchoolId ? "EDIT AUDIT REPORT" : activeTab.replace('-', ' ').toUpperCase()}
            </h2>
          </div>
          <div className="admin-user-group">
            <div className="notification-bell-wrapper">
              <div className="notification-bell" onClick={() => setShowBellDropdown(!showBellDropdown)}>
                <span style={{ fontSize: '1.2rem' }}>🔔</span>
                {pendingTasks.filter(isTaskActionable).length > 0 && (
                  <div className={`notification-badge ${pendingTasks.some(t => isTaskActionable(t) && t.priority === 'red') ? 'pulse' : ''}`}>
                    {pendingTasks.filter(isTaskActionable).length}
                  </div>
                )}
              </div>
              {showBellDropdown && (
                <div className="bell-dropdown">
                  <div className="bell-dropdown-header">My actions & notifications</div>
                  <div className="bell-dropdown-list">
                    {pendingTasks.length === 0 ? (
                      <div className="bell-empty">Nothing to show</div>
                    ) : (
                      pendingTasks.map((task, i) => (
                        <div 
                          key={i} 
                          className={`bell-item priority-${task.priority}`}
                          onClick={() => handleBellItemClick(task)}
                        >
                          <div
                            aria-hidden="true"
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '999px',
                              background: bellTrafficLightColor(task),
                              boxShadow: '0 0 0 2px rgba(15, 23, 42, 0.35) inset',
                              flex: '0 0 auto',
                              marginTop: '2px'
                            }}
                          />
                          <div className="bell-item-content">
                            <div className="bell-item-label">{task.label}</div>
                            <div className="bell-item-meta">
                              {(task.category || 'system').toUpperCase()}
                              {!isTaskActionable(task) ? ' · FYI' : ''}
                            </div>
                          </div>
                          {task.docId && (
                            <button 
                              className="bell-dismiss-btn"
                              onClick={(e) => handleDismissTask(e, task)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="bell-dropdown-footer" onClick={() => { navigateToTab('overview'); setShowBellDropdown(false); }}>
                    Go to My Action Center
                  </div>
                  <div style={{ padding: '10px 12px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearAllInformationalNotifications();
                      }}
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Clear all notifications
                    </button>
                    <div style={{ marginTop: '8px', fontSize: '0.7rem', opacity: 0.55, lineHeight: 1.35 }}>
                      This clears dismissible FYI items (including “resolved by another admin”). It does not remove Action Centre tasks tied to live registry state.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="support-help-wrapper">
              <SupportHelpButton
                label="Help"
                onClick={() => {
                  if (permissions.canUseSupportAssistant) {
                    setShowHelpMenu((prev) => !prev);
                  } else {
                    setShowSupportModal(true);
                  }
                }}
              />
              {showHelpMenu && permissions.canUseSupportAssistant && (
                <div className="admin-help-menu">
                  <button
                    type="button"
                    className="admin-help-menu-item"
                    onClick={() => {
                      setShowSupportModal(true);
                      setShowHelpMenu(false);
                    }}
                  >
                    Report a problem
                  </button>
                  <button
                    type="button"
                    className="admin-help-menu-item admin-help-menu-item--accent"
                    onClick={openSupportAssistant}
                  >
                    Open Support Assistant
                  </button>
                </div>
              )}
            </div>
            <div className="admin-user-info">
              <div className="admin-user-name">{user.displayName}</div>
              <div className="admin-user-role">
                {staffRecord?.role?.replace('_', ' ') || 'Accreditor'}
              </div>
            </div>
            <button
              type="button"
              className="admin-sign-out-btn"
              onClick={logout}
            >
              Sign Out
            </button>
          </div>
        </header>

        <section className="admin-view">
          {dossierSchoolId ? (
            <SchoolDossier 
              schoolId={dossierSchoolId} 
              onClose={() => setDossierSchoolId(null)} 
              onEditReport={handleEditReport}
            />
          ) : editingSchoolId ? (
            <ReportEditor 
              schoolId={editingSchoolId} 
              onClose={closeEditor} 
              onOpenDossier={handleOpenDossier}
              onDismiss={handleDismissTask}
            />
          ) : (
            <>
              {activeTab === 'overview' && (
                <OverviewDashboard 
                  tasks={pendingTasks} 
                  onTaskAction={handleTaskAction}
                  onDismiss={handleDismissTask}
                  onClearAllInformational={handleClearAllInformationalNotifications}
                />
              )}
              {activeTab === 'registry' && (
                <SchoolDirectory 
                  onEditReport={handleEditReport} 
                  onOpenDossier={handleOpenDossier} 
                  showToast={showToast}
                  initialExpandedId={lastActiveId}
                  permissions={permissions}
                  currentUserUid={user?.uid || null}
                />
              )}
              
              {activeTab === 'payments' && permissions.canManagePayments && <AdminPricing />}
              {activeTab === 'finance' && permissions.canManagePayments && <AdminFinance />}
              {activeTab === 'my-earnings' && isApproved && <StaffEarnings />}
              {activeTab === 'public-showcase' && permissions.canManagePublicShowcase && <AdminPublicShowcase />}
              {activeTab === 'audit-trail' && permissions.canViewAuditTrail && <AuditTrail />}
              {activeTab === 'staff' && permissions.canManageStaff && <StaffManagement />}
              {activeTab === 'portal-languages' && permissions.canManagePortalLanguages && <PortalLanguages />}
              {activeTab === 'ai-pipeline' && permissions.canManageStaff && <AdminAiPipeline />}
              {activeTab === 'support-assistant' && permissions.canUseSupportAssistant && (
                <AdminSupportAssistant
                  onOpenRegistryIssue={(schoolId) => {
                    navigateToTab('registry');
                    setLastActiveId(schoolId);
                  }}
                  onOpenReportIssue={handleEditReport}
                />
              )}
              {activeTab === 'emails' && permissions.canManageStaff && <CommsLab />}
              {activeTab === 'profile' && <AuditorProfile />}
            </>
          )}
        </section>
        <SupportIssueModal
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          sourceSurface="admin_portal"
          context={adminSupportContext}
          currentUser={user}
          permissionSnapshot={permissions}
        />
      </main>
    </div>
  );
};

const AdminPortal = () => (
  <UserProvider>
    <AdminPortalContent />
  </UserProvider>
);

export default AdminPortal;